import { io, Socket } from 'socket.io-client';
import { HOST } from '@api/constant';
import getId from '../../api/getId';

type ConnectionState =
	| 'DISCONNECTED'
	| 'CONNECTING'
	| 'CONNECTED'
	| 'RECONNECTING'
	| 'ERROR';

interface QueuedOperation {
	event: string;
	data: any;
	timestamp: number;
}

class SocketManager {
	private static instance: SocketManager;
	private videoSocket: Socket | null = null;
	private connectionState: ConnectionState = 'DISCONNECTED';
	private connectionPromise: Promise<Socket | null> | null = null;
	private reconnectAttempts = 0;
	private maxReconnectAttempts = Infinity; // Unlimited reconnects for persistent connection
	private reconnectDelay = 1000; // Start with 1 second
	private maxReconnectDelay = 30000; // Max 30 seconds
	private heartbeatInterval: NodeJS.Timeout | null = null;
	private connectionTimeout: NodeJS.Timeout | null = null;
	private operationQueue: QueuedOperation[] = [];
	private eventListeners: Map<string, Set<Function>> = new Map();
	private shouldReconnect = true;
	private lastHeartbeat: number = Date.now();
	private heartbeatTimeout = 30000; // 30 seconds without heartbeat = stale connection

	// Connection state callbacks
	private connectionStateCallbacks: Set<(state: ConnectionState) => void> =
		new Set();

	private constructor() {}

	static getInstance(): SocketManager {
		if (!SocketManager.instance) {
			SocketManager.instance = new SocketManager();
		}
		return SocketManager.instance;
	}

	/**
	 * Connect to the socket server with automatic reconnection
	 */
	connect(): Promise<Socket | null> {
		// Return existing promise if already connecting
		if (this.connectionPromise) {
			return this.connectionPromise;
		}

		// Return immediately if already connected
		if (
			this.videoSocket?.connected &&
			this.connectionState === 'CONNECTED'
		) {
			return Promise.resolve(this.videoSocket);
		}

		this.connectionPromise = this.attemptConnection();
		return this.connectionPromise;
	}

	/**
	 * Internal method to attempt connection
	 */
	private attemptConnection(): Promise<Socket | null> {
		return new Promise((resolve, reject) => {
			try {
				const userId = getId();
				if (!userId) {
					const error = new Error(
						'User not authenticated. Cannot connect socket without userId.'
					);
					console.error('❌ [SocketManager]', error.message);
					this.setConnectionState('ERROR');
					this.connectionPromise = null;
					reject(error);
					return;
				}

				// Clean up existing socket if present
				this.cleanupSocket();

				this.setConnectionState('CONNECTING');
				const socketUrl = `${HOST}/call`;

				console.log(
					'🔌 [SocketManager] Attempting to connect to:',
					socketUrl
				);

				const socketOptions: any = {
					withCredentials: true,
					transports: ['websocket', 'polling'],
					forceNew: false, // Reuse connection if possible
					timeout: 15000, // 15 second timeout
					reconnection: false, // We handle reconnection manually
					reconnectionAttempts: 0,
					reconnectionDelay: 0,
					// Pass userId in auth object for mobile authentication
					auth: userId ? { userId: String(userId) } : undefined,
					// Also pass userId in query as fallback
					query: userId ? { userId: String(userId) } : undefined,
				};

				this.videoSocket = io(socketUrl, socketOptions);
				this.setupSocketEventHandlers(resolve, reject);

				// Set connection timeout
				this.connectionTimeout = setTimeout(() => {
					if (this.connectionState !== 'CONNECTED') {
						console.error('❌ [SocketManager] Connection timeout');
						this.setConnectionState('ERROR');
						this.connectionPromise = null;
						reject(new Error('Connection timeout'));
					}
				}, 20000); // 20 second timeout
			} catch (error) {
				console.error(
					'❌ [SocketManager] Failed to initialize socket:',
					error
				);
				this.setConnectionState('ERROR');
				this.connectionPromise = null;
				reject(error);
			}
		});
	}

	/**
	 * Setup socket event handlers
	 */
	private setupSocketEventHandlers(
		resolve: (socket: Socket | null) => void,
		reject: (error: any) => void
	): void {
		if (!this.videoSocket) return;

		// Remove any existing listeners to prevent duplicates
		this.videoSocket.removeAllListeners();

		// Connection successful
		this.videoSocket.on('connect', () => {
			console.log(
				'✅ [SocketManager] Connected to video socket successfully!'
			);
			console.log('🔌 [SocketManager] Socket ID:', this.videoSocket?.id);

			if (this.connectionTimeout) {
				clearTimeout(this.connectionTimeout);
				this.connectionTimeout = null;
			}

			this.setConnectionState('CONNECTED');
			this.reconnectAttempts = 0;
			this.reconnectDelay = 1000; // Reset delay
			this.connectionPromise = null;

			// Start heartbeat
			this.startHeartbeat();

			// Process queued operations
			this.processOperationQueue();

			resolve(this.videoSocket);
		});

		// Disconnection
		this.videoSocket.on('disconnect', (reason) => {
			console.log('❌ [SocketManager] Disconnected. Reason:', reason);
			this.setConnectionState('DISCONNECTED');
			this.stopHeartbeat();

			// Don't reconnect if manually disconnected
			if (reason === 'io client disconnect') {
				this.shouldReconnect = false;
				return;
			}

			// Attempt reconnection
			if (this.shouldReconnect) {
				this.scheduleReconnect();
			}
		});

		// Connection error
		this.videoSocket.on('connect_error', (error) => {
			console.error('❌ [SocketManager] Connection error:', {
				message: error.message,
				type: error.type,
				description: error.description,
			});

			if (this.connectionTimeout) {
				clearTimeout(this.connectionTimeout);
				this.connectionTimeout = null;
			}

			// FIX: Don't retry if error is DUPLICATE_CONNECTION - this means there's already an active connection
			if (error.message === 'DUPLICATE_CONNECTION') {
				console.log('ℹ️ [SocketManager] Duplicate connection detected - already have active connection, not retrying');
				this.setConnectionState('CONNECTED'); // Consider ourselves connected since there's already an active connection
				if (this.connectionPromise) {
					this.connectionPromise = null;
				}
				// Resolve the promise since we consider this a success (connection exists)
				if (this.connectionState === 'CONNECTING') {
					resolve(this.videoSocket);
				}
				// Don't schedule reconnection - we already have a connection
				return;
			}

			// Don't reject immediately - let reconnection handle it
			if (this.connectionState === 'CONNECTING') {
				this.setConnectionState('ERROR');
				this.connectionPromise = null;
				reject(error);
			}

			// Schedule reconnection if not manually disconnected
			if (this.shouldReconnect) {
				this.scheduleReconnect();
			}
		});

		// Socket error
		this.videoSocket.on('error', (error) => {
			console.error('❌ [SocketManager] Socket error:', error);
			this.setConnectionState('ERROR');
		});

		// Reconnection events (if socket.io's reconnection is enabled)
		this.videoSocket.on('reconnect', (attemptNumber) => {
			console.log(
				'✅ [SocketManager] Reconnected after',
				attemptNumber,
				'attempts'
			);
			this.setConnectionState('CONNECTED');
			this.reconnectAttempts = 0;
			this.reconnectDelay = 1000;
			this.startHeartbeat();
			this.processOperationQueue();
		});

		this.videoSocket.on('reconnect_attempt', (attemptNumber) => {
			console.log(
				'🔄 [SocketManager] Reconnection attempt',
				attemptNumber
			);
			this.setConnectionState('RECONNECTING');
		});

		this.videoSocket.on('reconnect_error', (error) => {
			console.error('❌ [SocketManager] Reconnection error:', error);
		});

		this.videoSocket.on('reconnect_failed', () => {
			console.error('❌ [SocketManager] Reconnection failed');
			this.setConnectionState('ERROR');
			// Will be handled by our manual reconnection logic
		});

		// Register all stored event listeners
		this.reregisterEventListeners();
	}

	/**
	 * Schedule reconnection with exponential backoff
	 */
	private scheduleReconnect(): void {
		if (!this.shouldReconnect) return;
		if (
			this.connectionState === 'CONNECTING' ||
			this.connectionState === 'RECONNECTING'
		) {
			return; // Already attempting to connect
		}

		if (this.reconnectAttempts >= this.maxReconnectAttempts) {
			console.error(
				'❌ [SocketManager] Max reconnection attempts reached. Stopping reconnection.'
			);
			this.setConnectionState('ERROR');
			return;
		}

		this.reconnectAttempts++;
		const delay = Math.min(
			this.reconnectDelay * Math.pow(2, this.reconnectAttempts - 1),
			this.maxReconnectDelay
		);

		console.log(
			`🔄 [SocketManager] Scheduling reconnection attempt ${this.reconnectAttempts} in ${delay}ms`
		);

		this.setConnectionState('RECONNECTING');

		setTimeout(() => {
			if (this.shouldReconnect && this.connectionState !== 'CONNECTED') {
				this.connectionPromise = this.attemptConnection();
			}
		}, delay);
	}

	/**
	 * Start heartbeat to detect stale connections
	 */
	private startHeartbeat(): void {
		this.stopHeartbeat();

		this.lastHeartbeat = Date.now();

		// Listen for pong from server
		if (this.videoSocket) {
			this.videoSocket.on('pong', () => {
				this.lastHeartbeat = Date.now();
			});
		}

		// Send ping every 10 seconds
		this.heartbeatInterval = setInterval(() => {
			if (this.videoSocket?.connected) {
				// Check if connection is stale
				const timeSinceLastHeartbeat = Date.now() - this.lastHeartbeat;
				if (timeSinceLastHeartbeat > this.heartbeatTimeout) {
					console.warn(
						'⚠️ [SocketManager] Connection appears stale. Reconnecting...'
					);
					this.cleanupSocket();
					if (this.shouldReconnect) {
						this.scheduleReconnect();
					}
					return;
				}

				// Send ping
				this.videoSocket.emit('ping');
			}
		}, 10000);
	}

	/**
	 * Stop heartbeat
	 */
	private stopHeartbeat(): void {
		if (this.heartbeatInterval) {
			clearInterval(this.heartbeatInterval);
			this.heartbeatInterval = null;
		}
	}

	/**
	 * Cleanup socket and remove all listeners
	 */
	private cleanupSocket(): void {
		if (this.videoSocket) {
			this.videoSocket.removeAllListeners();
			if (this.videoSocket.connected) {
				this.videoSocket.disconnect();
			}
			this.videoSocket = null;
		}

		if (this.connectionTimeout) {
			clearTimeout(this.connectionTimeout);
			this.connectionTimeout = null;
		}

		this.stopHeartbeat();
	}

	/**
	 * Set connection state and notify callbacks
	 */
	private setConnectionState(state: ConnectionState): void {
		if (this.connectionState !== state) {
			this.connectionState = state;
			this.connectionStateCallbacks.forEach((callback) =>
				callback(state)
			);
		}
	}

	/**
	 * Get current connection state
	 */
	getConnectionState(): ConnectionState {
		return this.connectionState;
	}

	/**
	 * Subscribe to connection state changes
	 */
	onConnectionStateChange(
		callback: (state: ConnectionState) => void
	): () => void {
		this.connectionStateCallbacks.add(callback);
		// Return unsubscribe function
		return () => {
			this.connectionStateCallbacks.delete(callback);
		};
	}

	/**
	 * Disconnect from socket
	 */
	disconnect(): void {
		this.shouldReconnect = false;
		this.cleanupSocket();
		this.setConnectionState('DISCONNECTED');
		this.operationQueue = [];
		this.connectionPromise = null;
	}

	/**
	 * Check if socket is connected
	 */
	isSocketConnected(): boolean {
		return (
			this.connectionState === 'CONNECTED' &&
			this.videoSocket?.connected === true
		);
	}

	/**
	 * Get socket instance
	 */
	getSocket(): Socket | null {
		return this.videoSocket;
	}

	/**
	 * Queue operation for when connection is restored
	 */
	private queueOperation(event: string, data: any): void {
		this.operationQueue.push({
			event,
			data,
			timestamp: Date.now(),
		});

		// Limit queue size to prevent memory issues
		if (this.operationQueue.length > 100) {
			this.operationQueue.shift();
		}
	}

	/**
	 * Process queued operations
	 */
	private processOperationQueue(): void {
		if (!this.isSocketConnected() || this.operationQueue.length === 0) {
			return;
		}

		console.log(
			`📤 [SocketManager] Processing ${this.operationQueue.length} queued operations`
		);

		const operations = [...this.operationQueue];
		this.operationQueue = [];

		operations.forEach((op) => {
			// Only process operations less than 5 minutes old
			if (Date.now() - op.timestamp < 300000) {
				if (this.videoSocket) {
					this.videoSocket.emit(op.event, op.data);
				}
			}
		});
	}

	/**
	 * Safe emit - queues if disconnected
	 */
	private safeEmit(event: string, data: any): void {
		if (this.isSocketConnected() && this.videoSocket) {
			this.videoSocket.emit(event, data);
		} else {
			console.warn(
				`⚠️ [SocketManager] Socket not connected. Queuing operation: ${event}`
			);
			this.queueOperation(event, data);

			// Attempt to reconnect if not already connecting
			if (
				this.shouldReconnect &&
				this.connectionState !== 'CONNECTING' &&
				this.connectionState !== 'RECONNECTING'
			) {
				this.connect().catch((error) => {
					console.error(
						'❌ [SocketManager] Failed to reconnect:',
						error
					);
				});
			}
		}
	}

	/**
	 * Register event listener with deduplication
	 */
	private registerEventListener(event: string, callback: Function): void {
		if (!this.eventListeners.has(event)) {
			this.eventListeners.set(event, new Set());
		}
		this.eventListeners.get(event)!.add(callback);

		if (this.videoSocket) {
			this.videoSocket.on(event, callback as any);
		}
	}

	/**
	 * Remove event listener
	 */
	private removeEventListener(event: string, callback: Function): void {
		const listeners = this.eventListeners.get(event);
		if (listeners) {
			listeners.delete(callback);
			if (listeners.size === 0) {
				this.eventListeners.delete(event);
			}
		}

		if (this.videoSocket) {
			this.videoSocket.off(event, callback as any);
		}
	}

	/**
	 * Reregister all event listeners (useful after reconnection)
	 */
	private reregisterEventListeners(): void {
		if (!this.videoSocket) return;

		this.eventListeners.forEach((callbacks, event) => {
			callbacks.forEach((callback) => {
				this.videoSocket!.on(event, callback as any);
			});
		});
	}

	// Meeting room functions
	joinConference(data: {
		roomId: string;
		userId: string;
		displayName?: string;
		isGuest?: boolean;
		password?: string;
	}): void {
		const conferenceData: any = {
			roomId: data.roomId,
			userId: data.userId,
			displayName: data.displayName || 'User',
			isGuest: data.isGuest !== undefined ? data.isGuest : false,
		};

		if (data.password) {
			conferenceData.password = data.password;
		}

		console.log(
			'🔗 [SocketManager] Emitting join_conference:',
			conferenceData
		);
		this.safeEmit('join_conference', conferenceData);
	}

	leaveConference(roomId: string, userId?: string): void {
		this.safeEmit('leave_conference', { roomId, userId });
	}

	// Chat functions
	joinMeetingChat(meetingId: string, roomId: string, user: any): void {
		this.safeEmit('join_meeting_chat', { meetingId, roomId, user });
	}

	sendMeetingMessage(messageData: any): void {
		this.safeEmit('meeting_message', messageData);
	}

	sendUserTyping(typingData: any): void {
		this.safeEmit('user_typing', typingData);
	}

	getChatHistory(
		meetingId: string,
		roomId: string,
		limit: number = 50
	): void {
		this.safeEmit('get_chat_history', { meetingId, roomId, limit });
	}

	// WebRTC functions
	sendSDPOffer(offerData: any): void {
		this.safeEmit('sdp_offer', offerData);
	}

	sendSDPAnswer(answerData: any): void {
		this.safeEmit('sdp_answer', answerData);
	}

	sendICECandidate(candidateData: any): void {
		this.safeEmit('ice_candidate', candidateData);
	}

	// Call functions
	initiateCall(callData: any): void {
		this.safeEmit('initiate_call', callData);
	}

	acceptCall(callId: string): void {
		this.safeEmit('accept_call', { callId });
	}

	declineCall(callId: string): void {
		this.safeEmit('decline_call', { callId });
	}

	endCall(callId: string): void {
		this.safeEmit('end_call', { callId });
	}

	// Media control
	updateMediaState(mediaData: any): void {
		this.safeEmit('media_state_changed', mediaData);
	}

	// Event listeners - with proper management
	onConnect(callback: () => void): () => void {
		this.registerEventListener('connect', callback);
		return () => this.removeEventListener('connect', callback);
	}

	onDisconnect(callback: () => void): () => void {
		this.registerEventListener('disconnect', callback);
		return () => this.removeEventListener('disconnect', callback);
	}

	onError(callback: (error: any) => void): () => void {
		this.registerEventListener('error', callback);
		return () => this.removeEventListener('error', callback);
	}

	// Meeting events
	onIncomingCall(callback: (data: any) => void): () => void {
		this.registerEventListener('incoming_call', callback);
		return () => this.removeEventListener('incoming_call', callback);
	}

	onCallAccepted(callback: (data: any) => void): () => void {
		this.registerEventListener('call_accepted', callback);
		return () => this.removeEventListener('call_accepted', callback);
	}

	onCallDeclined(callback: (data: any) => void): () => void {
		this.registerEventListener('call_declined', callback);
		return () => this.removeEventListener('call_declined', callback);
	}

	onCallEnded(callback: (data: any) => void): () => void {
		this.registerEventListener('call_ended', callback);
		return () => this.removeEventListener('call_ended', callback);
	}

	// WebRTC events
	onSDPOffer(callback: (data: any) => void): () => void {
		this.registerEventListener('sdp_offer', callback);
		return () => this.removeEventListener('sdp_offer', callback);
	}

	onSDPAnswer(callback: (data: any) => void): () => void {
		this.registerEventListener('sdp_answer', callback);
		return () => this.removeEventListener('sdp_answer', callback);
	}

	onICECandidate(callback: (data: any) => void): () => void {
		this.registerEventListener('ice_candidate', callback);
		return () => this.removeEventListener('ice_candidate', callback);
	}

	onMediaStateChanged(callback: (data: any) => void): () => void {
		this.registerEventListener('media_state_changed', callback);
		return () => this.removeEventListener('media_state_changed', callback);
	}

	// Conference events
	onConferenceRoomCreated(callback: (data: any) => void): () => void {
		console.log(
			'📡 [SocketManager] Registering conference_room_created listener'
		);
		const wrappedCallback = (data: any) => {
			console.log(
				'🎊 [SocketManager] conference_room_created event received:',
				data
			);
			callback(data);
		};
		this.registerEventListener('conference_room_created', wrappedCallback);
		return () =>
			this.removeEventListener(
				'conference_room_created',
				wrappedCallback
			);
	}

	onConferenceJoined(callback: (data: any) => void): () => void {
		console.log(
			'📡 [SocketManager] Registering conference_joined listener'
		);
		const wrappedCallback = (data: any) => {
			console.log(
				'🚪 [SocketManager] conference_joined event received:',
				data
			);
			callback(data);
		};
		this.registerEventListener('conference_joined', wrappedCallback);
		return () =>
			this.removeEventListener('conference_joined', wrappedCallback);
	}

	onParticipantJoined(callback: (data: any) => void): () => void {
		console.log(
			'📡 [SocketManager] Registering participant_joined listener'
		);
		const wrappedCallback = (data: any) => {
			console.log(
				'🎉 [SocketManager] participant_joined event received:',
				data
			);
			callback(data);
		};
		this.registerEventListener('participant_joined', wrappedCallback);
		return () =>
			this.removeEventListener('participant_joined', wrappedCallback);
	}

	onParticipantLeft(callback: (data: any) => void): () => void {
		console.log('📡 [SocketManager] Registering participant_left listener');
		const wrappedCallback = (data: any) => {
			console.log(
				'👋 [SocketManager] participant_left event received:',
				data
			);
			callback(data);
		};
		this.registerEventListener('participant_left', wrappedCallback);
		return () =>
			this.removeEventListener('participant_left', wrappedCallback);
	}

	onConferenceError(callback: (data: any) => void): () => void {
		this.registerEventListener('conference_error', callback);
		return () => this.removeEventListener('conference_error', callback);
	}

	// WebRTC signaling events
	onOffer(callback: (data: any) => void): () => void {
		this.registerEventListener('offer', callback);
		return () => this.removeEventListener('offer', callback);
	}

	onAnswer(callback: (data: any) => void): () => void {
		this.registerEventListener('answer', callback);
		return () => this.removeEventListener('answer', callback);
	}

	onIceCandidate(callback: (data: any) => void): () => void {
		this.registerEventListener('ice-candidate', callback);
		return () => this.removeEventListener('ice-candidate', callback);
	}

	// WebRTC signaling senders
	sendOffer(data: any): void {
		this.safeEmit('offer', data);
	}

	sendAnswer(data: any): void {
		this.safeEmit('answer', data);
	}

	sendIceCandidate(data: any): void {
		this.safeEmit('ice-candidate', data);
	}

	// Chat events
	onMeetingMessage(callback: (data: any) => void): () => void {
		this.registerEventListener('meeting_message', callback);
		return () => this.removeEventListener('meeting_message', callback);
	}

	onChatHistory(callback: (data: any) => void): () => void {
		this.registerEventListener('chat_history', callback);
		return () => this.removeEventListener('chat_history', callback);
	}

	onUserTyping(callback: (data: any) => void): () => void {
		this.registerEventListener('user_typing', callback);
		return () => this.removeEventListener('user_typing', callback);
	}

	onUserJoinedChat(callback: (data: any) => void): () => void {
		this.registerEventListener('user_joined_chat', callback);
		return () => this.removeEventListener('user_joined_chat', callback);
	}

	onUserLeftChat(callback: (data: any) => void): () => void {
		this.registerEventListener('user_left_chat', callback);
		return () => this.removeEventListener('user_left_chat', callback);
	}

	onMessageSent(callback: (data: any) => void): () => void {
		this.registerEventListener('message_sent', callback);
		return () => this.removeEventListener('message_sent', callback);
	}

	onChatError(callback: (data: any) => void): () => void {
		this.registerEventListener('chat_error', callback);
		return () => this.removeEventListener('chat_error', callback);
	}

	// Data Collection Events
	requestDataCollection(data: {
		roomId: string;
		dataId: string;
		dataTitle: string;
		dataType: string;
		schema?: any;
		requestedBy: {
			userId: string;
			displayName: string;
		};
	}): void {
		console.log(
			'🔗 [SocketManager] Emitting request_data_collection:',
			data
		);
		this.safeEmit('request_data_collection', data);
	}

	onDataFormRequested(callback: (data: any) => void): () => void {
		this.registerEventListener('show_data_form', callback);
		return () => this.removeEventListener('show_data_form', callback);
	}

	submitDataCollectionResponse(data: {
		roomId: string;
		dataId: string;
		responseId: string;
		formData: any;
		respondent: {
			userId: string;
			displayName: string;
		};
	}): void {
		console.log(
			'🔗 [SocketManager] Emitting data_collection_response:',
			data
		);
		this.safeEmit('data_collection_response', data);
	}

	onDataCollectionResponse(callback: (data: any) => void): () => void {
		this.registerEventListener('data_collection_response', callback);
		return () =>
			this.removeEventListener('data_collection_response', callback);
	}

	onDataCollectionComplete(callback: (data: any) => void): () => void {
		this.registerEventListener('data_collection_complete', callback);
		return () =>
			this.removeEventListener('data_collection_complete', callback);
	}

	// Remove all listeners
	removeAllListeners(): void {
		if (this.videoSocket) {
			this.videoSocket.removeAllListeners();
		}
		this.eventListeners.clear();
	}

	/**
	 * Enable automatic reconnection (default: true)
	 */
	enableReconnection(): void {
		this.shouldReconnect = true;
	}

	/**
	 * Disable automatic reconnection
	 */
	disableReconnection(): void {
		this.shouldReconnect = false;
	}
}

export default SocketManager;
